/**
 * View-specific ordering utilities
 * Manages independent ordering for Kanban and Gantt views
 */

import type { ProjectNode } from '@/shared/types/projectData';
import { getCoreKanbanColumnIdForStatus, normalizeKanbanColumnId } from '@/shared/utils/kanbanState';

/**
 * Get the Kanban-specific order for a node
 * Falls back to general order if viewState is not available
 */
export function getKanbanOrder(node: ProjectNode): number {
  if (node.viewState?.kanban?.order !== undefined) {
    return node.viewState.kanban.order;
  }
  // Fallback to general order for backward compatibility
  return node.order ?? 0;
}

/**
 * Get the Gantt-specific order for a node
 * Falls back to general order if viewState is not available
 */
export function getGanttOrder(node: ProjectNode): number {
  // Prefer canonical order persisted by backend
  if (node.order !== undefined && node.order !== null) {
    return node.order as number;
  }
  // Fallback to legacy/custom viewState order
  if (node.viewState?.gantt?.customOrder !== undefined) {
    return node.viewState.gantt.customOrder;
  }
  return 0;
}

/**
 * Set the Gantt-specific order for a node
 * Ensures viewState structure exists
 */
export function setGanttOrder(node: ProjectNode, order: number): void {
  // Ensure viewState exists
  if (!node.viewState) {
    node.viewState = {};
  }
  
  // Ensure gantt section exists
  if (!node.viewState.gantt) {
    node.viewState.gantt = {
      expanded: false,
      customOrder: order
    };
  } else {
    node.viewState.gantt.customOrder = order;
  }
}

/**
 * Initialize viewState for a node if it doesn't exist
 * Preserves existing order values
 */
export function initializeViewState(node: ProjectNode): void {
  // Initialize viewState if it doesn't exist
  if (!node.viewState) {
    node.viewState = {};
  }
  
  // Initialize kanban section with current order
  if (!node.viewState.kanban) {
    node.viewState.kanban = {
      columnId: getCoreKanbanColumnIdForStatus(node.status),
      order: node.order ?? 0
    };
  } else if (node.viewState.kanban.order === undefined) {
    node.viewState.kanban.columnId = normalizeKanbanColumnId(node.viewState.kanban.columnId);
    node.viewState.kanban.order = node.order ?? 0;
  }
  
  // Initialize gantt section with current order
  if (!node.viewState.gantt) {
    node.viewState.gantt = {
      expanded: false,
      customOrder: node.order ?? 0
    };
  } else if (node.viewState.gantt.customOrder === undefined) {
    node.viewState.gantt.customOrder = node.order ?? 0;
  }
}
